import {
    Color,
    EyesColors,
    Hair,
    HairColors,
    Nose,
    Pants,
    PantsColors,
    RaceColors,
    RaceNoses,
    Sex,
    SexHair,
    SexPants,
    SexShirts,
    SexShoes,
    SexTypes,
    Shirt,
    ShirtColors,
    Shoe,
    ShoeColors,
} from "game-common/character/character"
import {
    ItemClass,
    ItemTypeMeta,
    addInventoryItem,
    locateInventoryActiveItem,
    locateInventoryActiveItemObject,
    removeInventoryItem,
} from "game-common/item/item"
import { Entity } from "game-common/models"
import { randomId } from "game-common/util"

import { SwatchFamily, prettyLabel, swatchFor } from "./theme"

// The character-creation screen's model of "what can I change, and what are my choices?".
//
// Every trait answers with the same shape -- one or two palettes of options -- so the picker only
// ever has to draw a grid of labelled chips or a grid of colour swatches, whichever a section asks
// for. Pure: no pixi, no renderer, so both halves (the options offered, and what picking one does to
// the entity) are unit-tested.

export const NONE = "none"

export const TRAIT_IDS = ["sex", "skin", "hair", "eyes", "nose", "shirt", "pants", "shoes"] as const

export type TraitId = (typeof TRAIT_IDS)[number]

export type SectionKind = "style" | "color"

export interface TraitOption {
    value: string
    label: string
    // Present on colour options: the chip is drawn as this colour rather than as a label.
    swatch?: number
}

export interface TraitSection {
    kind: SectionKind
    title: string
    options: TraitOption[]
    selected: string
}

export interface TraitView {
    id: TraitId
    label: string
    icon: string
    // The current choice, shown small and dim beside the trait's name.
    summary: string
    // Colour of the trait's current tint, for the dot on the trait row (absent for style-only traits).
    swatch?: number
    sections: TraitSection[]
}

const TRAIT_META: Record<TraitId, { label: string; icon: string }> = {
    sex: { label: "Build", icon: "🧍" },
    skin: { label: "Skin", icon: "🎨" },
    hair: { label: "Hair", icon: "💇" },
    eyes: { label: "Eyes", icon: "👁" },
    nose: { label: "Nose", icon: "👃" },
    shirt: { label: "Shirt", icon: "👕" },
    pants: { label: "Legs", icon: "👖" },
    shoes: { label: "Feet", icon: "👢" },
}

const GARMENT_CLASS: Partial<Record<TraitId, ItemClass>> = {
    shirt: "shirt",
    pants: "pants",
    shoes: "shoes",
}

// Colours a given garment type can be painted.
const garmentColors = (itemClass: ItemClass, type?: string): Color[] => {
    if (!type || type === NONE) {
        return []
    }
    if (itemClass === "shirt") {
        const colors = ShirtColors[type as Shirt] || []
        // The forge's generated human bodies can dye a plain longsleeve grey even though the
        // legacy male LPC sheet has no grey variant.
        return type === "longsleeve" ? Array.from(new Set([...colors, "gray"])) : colors
    }
    if (itemClass === "pants") {
        return PantsColors[type as Pants] || []
    }
    if (itemClass === "shoes") {
        return ShoeColors[type as Shoe] || []
    }
    return []
}

// Types a garment can be, for this character's sex. Shirts and shoes may be left off; trousers of
// some kind are not optional.
const garmentTypes = (itemClass: ItemClass, sex: Sex): string[] => {
    if (itemClass === "shirt") {
        return [...SexShirts[sex], NONE]
    }
    if (itemClass === "pants") {
        return [...SexPants[sex]]
    }
    if (itemClass === "shoes") {
        return [...SexShoes[sex], NONE]
    }
    return []
}

const styleSection = (title: string, values: string[], selected?: string): TraitSection => ({
    kind: "style",
    title,
    options: values.map(value => ({ value, label: prettyLabel(value) })),
    selected: selected || NONE,
})

const colorSection = (
    title: string,
    family: SwatchFamily,
    values: Color[],
    selected?: Color,
): TraitSection => ({
    kind: "color",
    title,
    options: values.map(value => ({ value, label: prettyLabel(value), swatch: swatchFor(family, value) })),
    selected: selected || "",
})

// What the trait list shows for one trait, and what its palette offers.
export const describeTrait = (entity: Entity, id: TraitId): TraitView => {
    const { label, icon } = TRAIT_META[id]
    const appearance = entity.appearance
    const sex = appearance.sex
    const race = appearance.race

    const view = (summary: string, sections: TraitSection[], swatch?: number): TraitView => ({
        id,
        label,
        icon,
        summary,
        swatch,
        sections,
    })

    if (id === "sex") {
        return view(prettyLabel(sex), [styleSection("Build", [...SexTypes], sex)])
    }

    if (id === "skin") {
        const colors = (RaceColors[race] || []) as Color[]
        return view(
            prettyLabel(appearance.bodyColor),
            [colorSection("Skin tone", "skin", colors, appearance.bodyColor)],
            swatchFor("skin", appearance.bodyColor),
        )
    }

    if (id === "hair") {
        const styles = [NONE, ...SexHair[sex]]
        const sections = [styleSection("Style", styles, appearance.hair || NONE)]
        if (appearance.hair) {
            sections.push(
                colorSection("Colour", "hair", HairColors[appearance.hair] as Color[], appearance.hairColor),
            )
        }
        return view(
            prettyLabel(appearance.hair || NONE),
            sections,
            appearance.hair ? swatchFor("hair", appearance.hairColor) : undefined,
        )
    }

    if (id === "eyes") {
        const colors = (EyesColors[appearance.eyes || "normal"] || []) as Color[]
        return view(
            prettyLabel(appearance.eyesColor),
            [colorSection("Eye colour", "cloth", colors, appearance.eyesColor)],
            swatchFor("cloth", appearance.eyesColor),
        )
    }

    if (id === "nose") {
        const noses = [NONE, ...((RaceNoses[race] || []) as string[])]
        return view(prettyLabel(appearance.nose || NONE), [styleSection("Shape", noses, appearance.nose || NONE)])
    }

    // shirt / pants / shoes
    const itemClass = GARMENT_CLASS[id]
    const worn = locateInventoryActiveItem(entity.inventory, itemClass)
    const type = worn?.type
    const sections = [styleSection("Cut", garmentTypes(itemClass, sex), type || NONE)]
    const colors = garmentColors(itemClass, type)
    if (colors.length > 0) {
        sections.push(colorSection("Colour", "cloth", colors, worn?.color))
    }
    return view(
        prettyLabel(type || NONE),
        sections,
        worn?.color ? swatchFor("cloth", worn.color) : undefined,
    )
}

export const describeTraits = (entity: Entity): TraitView[] => TRAIT_IDS.map(id => describeTrait(entity, id))

// Swap what the character wears in one slot. Removing first keeps a single active item per class,
// and both helpers refresh the inventory-appearance hash the server reads.
const equip = (entity: Entity, itemClass: ItemClass, type?: string, preferredColor?: Color) => {
    const existing = locateInventoryActiveItemObject(entity.inventory, itemClass)
    if (existing) {
        removeInventoryItem(entity, existing.id)
    }
    if (!type || type === NONE) {
        return
    }
    const colors = garmentColors(itemClass, type)
    const color = preferredColor && colors.includes(preferredColor) ? preferredColor : colors[0]
    const itemTypeMeta: ItemTypeMeta = { type: type as any, class: itemClass, color }
    addInventoryItem(entity, {
        id: randomId(),
        itemType: "none",
        itemTypeMeta,
        quantity: 1,
        active: true,
    })
}

// Sex decides which hairstyles and which cuts of clothing exist, so a switch has to re-home anything
// the new body can't wear -- keeping the colour wherever the replacement supports it.
export const reconcileForSex = (entity: Entity) => {
    const appearance = entity.appearance
    const sex = appearance.sex

    if (appearance.hair && !SexHair[sex].includes(appearance.hair)) {
        appearance.hair = SexHair[sex][0]
    }

    ;(["shirt", "pants", "shoes"] as ItemClass[]).forEach(itemClass => {
        const worn = locateInventoryActiveItem(entity.inventory, itemClass)
        const allowed = garmentTypes(itemClass, sex)
        if (!worn) {
            // Trousers are the one slot nobody goes without.
            if (itemClass === "pants") {
                equip(entity, itemClass, allowed[0])
            }
            return
        }
        if (!allowed.includes(worn.type)) {
            equip(entity, itemClass, allowed[0], worn.color)
        }
    })
}

// Apply one palette click. Mutates the (builder-local) entity in place.
export const applyTraitChoice = (entity: Entity, id: TraitId, kind: SectionKind, value: string) => {
    const appearance = entity.appearance

    if (id === "sex") {
        appearance.sex = value as Sex
        reconcileForSex(entity)
        return
    }

    if (id === "skin") {
        appearance.bodyColor = value as Color
        // The nose is a separate sheet tinted to match the body; left alone it floats as a different
        // colour patch on the face.
        appearance.noseColor = value as Color
        return
    }

    if (id === "hair") {
        if (kind === "color") {
            appearance.hairColor = value as Color
            return
        }
        if (value === NONE) {
            appearance.hair = undefined
            return
        }
        appearance.hair = value as Hair
        const colors = HairColors[appearance.hair] as Color[]
        if (!colors?.includes(appearance.hairColor)) {
            appearance.hairColor = colors?.[0]
        }
        return
    }

    if (id === "eyes") {
        appearance.eyesColor = value as Color
        return
    }

    if (id === "nose") {
        appearance.nose = value === NONE ? undefined : (value as Nose)
        appearance.noseColor = appearance.bodyColor
        return
    }

    const itemClass = GARMENT_CLASS[id]
    const worn = locateInventoryActiveItem(entity.inventory, itemClass)
    if (kind === "color") {
        equip(entity, itemClass, worn?.type, value as Color)
        return
    }
    equip(entity, itemClass, value, worn?.color)
}
